import mongoose from "mongoose"
import { accessibleBy } from "@casl/mongoose"

import type { AclAction, AclSubjectType, AppAbility } from "./types"


type StoredAcl = {
  ability: AppAbility
  action?: AclAction
}

type QueryOptionsLike = Record<string, unknown>

type PipelineStageLike = Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value)

const mergeMongoQuery = (left: unknown, right: Record<string, unknown>): Record<string, unknown> => {
  const leftQuery = isRecord(left) ? left : {}
  if (Object.keys(leftQuery).length === 0) return right
  if (Object.keys(right).length === 0) return leftQuery
  return { $and: [leftQuery, right] }
}

const getQueryOptions = (query: unknown): QueryOptionsLike | undefined => {
  if (!query || typeof query !== "object") return undefined
  if (!("getOptions" in query) || typeof (query as { getOptions?: unknown }).getOptions !== "function") return undefined
  return (query as { getOptions: () => QueryOptionsLike }).getOptions()
}

const getStoredAclFromQuery = (query: unknown): StoredAcl | null => {
  const options = getQueryOptions(query)
  const raw = options?.rbAcl
  if (!isRecord(raw)) return null
  if (!("ability" in raw)) return null
  return raw as unknown as StoredAcl
}

const getStoredAclFromAggregate = (aggregate: unknown): StoredAcl | null => {
  if (!aggregate || typeof aggregate !== "object") return null
  const raw = (aggregate as { options?: unknown }).options
  if (!isRecord(raw)) return null
  const acl = raw.rbAcl
  if (!isRecord(acl)) return null
  if (!("ability" in acl)) return null
  return acl as unknown as StoredAcl
}

const addQueryAclFilter = (query: mongoose.Query<unknown, unknown>, action: AclAction): void => {
  const storedAcl = getStoredAclFromQuery(query)
  if (!storedAcl) return

  const ability = storedAcl.ability
  const resolvedAction = storedAcl.action ?? action
  const modelName = query.model.modelName

  const accessQuery = accessibleBy(ability, resolvedAction).ofType(modelName as AclSubjectType)
  query.and([accessQuery])
}

const injectAggregateMatch = (pipeline: PipelineStageLike[], match: Record<string, unknown>): void => {
  if (pipeline.length === 0) {
    pipeline.unshift({ $match: match })
    return
  }

  const first = pipeline[0]
  if (!isRecord(first)) {
    pipeline.unshift({ $match: match })
    return
  }

  if ("$geoNear" in first) {
    const geoNear = first.$geoNear
    if (isRecord(geoNear)) {
      geoNear.query = mergeMongoQuery(geoNear.query, match)
      return
    }
  }

  if ("$search" in first || "$vectorSearch" in first || "$searchMeta" in first) {
    pipeline.splice(1, 0, { $match: match })
    return
  }

  pipeline.unshift({ $match: match })
}

const addAggregateAclFilter = (aggregate: mongoose.Aggregate<unknown>, action: AclAction): void => {
  const storedAcl = getStoredAclFromAggregate(aggregate)
  if (!storedAcl) return

  const ability = storedAcl.ability
  const resolvedAction = storedAcl.action ?? action
  const modelName = aggregate.model().modelName

  const accessQuery = accessibleBy(ability, resolvedAction).ofType(modelName as AclSubjectType)
  injectAggregateMatch(aggregate.pipeline() as unknown as PipelineStageLike[], accessQuery)
}

const patchAggregateAcl = (): void => {
  const globalKey = Symbol.for("@rpcbase/db/acl/mongooseAggregateAclPatched")
  const globalState = globalThis as unknown as Record<string | symbol, unknown>
  if (globalState[globalKey]) return
  globalState[globalKey] = true

  const AggregatePrototype = (mongoose.Aggregate as unknown as { prototype: Record<string, unknown> }).prototype
  if (typeof AggregatePrototype.acl === "function") return

  AggregatePrototype.acl = function(
    this: mongoose.Aggregate<unknown>,
    ability: AppAbility,
    action: AclAction = "read",
  ) {
    this.option({ rbAcl: { ability, action } })
    return this
  }
}

const createModelAclProxy = <TModel extends mongoose.Model<unknown>>(
  model: TModel,
  ability: AppAbility,
): TModel => {
  return new Proxy(model, {
    get(target, prop, receiver) {
      if (prop === "acl") {
        return () => {
          throw new Error(
            `Model "${target.modelName}" is already ACL-scoped. Do not call .acl(...) again.`,
          )
        }
      }

      const value = Reflect.get(target, prop, receiver) as unknown
      if (typeof value !== "function") return value
      return (...args: unknown[]) => {
        const result = Reflect.apply(value, target, args) as unknown
        if (result && typeof result === "object" && "acl" in result && typeof (result as { acl?: unknown }).acl === "function") {
          return (result as { acl: (ability: AppAbility) => unknown }).acl(ability)
        }
        return result
      }
    },
  }) as unknown as TModel
}

declare const mongooseAclPluginBrand: unique symbol

declare module "mongoose" {
  interface QueryOptions<DocType = unknown> {
    rbAcl?: StoredAcl;
    [mongooseAclPluginBrand]?: DocType;
  }

  interface AggregateOptions {
    rbAcl?: StoredAcl;
  }

  interface Query<
    ResultType,
    DocType,
    THelpers = {},
    RawDocType = unknown,
    QueryOp = "find",
    TDocOverrides = Record<string, never>,
  > {
    [mongooseAclPluginBrand]?: [ResultType, DocType, THelpers, RawDocType, QueryOp, TDocOverrides];
    acl: (ability: AppAbility, action?: AclAction) => this;
  }

  interface Aggregate<ResultType> {
    [mongooseAclPluginBrand]?: ResultType;
    acl: (ability: AppAbility, action?: AclAction) => this;
  }

  interface Model<
    TRawDocType,
    TQueryHelpers,
    TInstanceMethods,
    TVirtuals,
    THydratedDocumentType,
    TSchema,
    TLeanResultType,
  > {
    [mongooseAclPluginBrand]?: [TRawDocType, TQueryHelpers, TInstanceMethods, TVirtuals, THydratedDocumentType, TSchema, TLeanResultType];
    acl: (ability: AppAbility) => this;
  }
}

export const mongooseAclPlugin = (schema: mongoose.Schema): void => {
  patchAggregateAcl()

  ;(schema.query as Record<string, unknown>).acl = function(
    this: mongoose.Query<unknown, unknown>,
    ability: AppAbility,
    action?: AclAction,
  ) {
    this.setOptions({ rbAcl: { ability, action } })
    return this
  }

  ;(schema.statics as Record<string, unknown>).acl = function(
    this: mongoose.Model<unknown>,
    ability: AppAbility,
  ) {
    return createModelAclProxy(this, ability)
  }

  schema.pre("aggregate", function() {
    addAggregateAclFilter(this, "read")
  })

  schema.pre("countDocuments", function() {
    addQueryAclFilter(this, "read")
  })

  schema.pre("deleteMany", function() {
    addQueryAclFilter(this, "delete")
  })

  schema.pre("deleteOne", function() {
    addQueryAclFilter(this, "delete")
  })

  schema.pre("distinct", function() {
    addQueryAclFilter(this, "read")
  })

  schema.pre("find", function() {
    addQueryAclFilter(this, "read")
  })

  schema.pre("findOne", function() {
    addQueryAclFilter(this, "read")
  })

  schema.pre("findOneAndDelete", function() {
    addQueryAclFilter(this, "delete")
  })

  schema.pre("findOneAndReplace", function() {
    addQueryAclFilter(this, "update")
  })

  schema.pre("findOneAndUpdate", function() {
    addQueryAclFilter(this, "update")
  })

  schema.pre("replaceOne", function() {
    addQueryAclFilter(this, "update")
  })

  schema.pre("updateMany", function() {
    addQueryAclFilter(this, "update")
  })

  schema.pre("updateOne", function() {
    addQueryAclFilter(this, "update")
  })
}

export const _private = {
  createModelAclProxy,
  injectAggregateMatch,
  mergeMongoQuery,
}
